// src/pages/Buyer/Enquiries.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add,
  FilterList,
  MoreVert,
  Visibility,
  Chat,
  Payment,
  LocalShipping,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { enquiryAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerEnquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await enquiryAPI.getUserEnquiries();
      setEnquiries(response.data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    if (filter === 'all') return true;
    if (filter === 'pending') return enquiry.status === 'pending';
    if (filter === 'responded') return enquiry.status === 'responded';
    if (filter === 'confirmed') return enquiry.status === 'confirmed';
    if (filter === 'cancelled') return enquiry.status === 'cancelled';
    return true;
  });

  const handleMenuOpen = (event, enquiryId) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionId(enquiryId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionId(null);
  };

  const handleAction = (action) => {
    const enquiry = enquiries.find(e => e.id === selectedActionId);
    if (!enquiry) return;

    switch (action) {
      case 'view':
        navigate(`/buyer/enquiries/${selectedActionId}`);
        break;
      case 'chat':
        navigate(`/chat/${selectedActionId}`);
        break;
      case 'payment':
        navigate(`/buyer/payment/${selectedActionId}`);
        break;
      case 'shipping':
        navigate(`/buyer/shipping/${selectedActionId}`);
        break;
      case 'download':
        // Implement download functionality
        toast.success('Download feature coming soon');
        break;
    }
    
    handleMenuClose();
  };

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setDetailDialogOpen(true);
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'responded', label: 'Responded' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading enquiries...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            My Enquiries
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all your fruit enquiries
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/fruits')}
        >
          New Enquiry
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterList color="action" />
          <Typography variant="body1">Filter by:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {statusOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                onClick={() => setFilter(option.value)}
                color={filter === option.value ? 'primary' : 'default'}
                variant={filter === option.value ? 'filled' : 'outlined'}
                clickable
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Enquiries Table */}
      <Paper sx={{ p: 3 }}>
        {enquiries.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You haven't made any enquiries yet. Start by browsing our fruits collection.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => navigate('/fruits')}
            >
              Browse Fruits
            </Button>
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Enquiry ID</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEnquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} hover>
                    <TableCell>#{enquiry.id}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/fruits/${enquiry.fruit_id}`)}
                      >
                        View Product
                      </Button>
                    </TableCell>
                    <TableCell>{enquiry.quantity} containers</TableCell>
                    <TableCell>{formatDate(enquiry.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={enquiry.status}
                        color={getStatusColor(enquiry.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(enquiry.created_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(enquiry)}
                          title="View Details"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        
                        {enquiry.status !== 'cancelled' && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/chat/${enquiry.id}`)}
                              title="Chat"
                              color="primary"
                            >
                              <Chat fontSize="small" />
                            </IconButton>
                            
                            {enquiry.status === 'confirmed' && (
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/buyer/payment/${enquiry.id}`)}
                                title="Payment"
                                color="secondary"
                              >
                                <Payment fontSize="small" />
                              </IconButton>
                            )}
                            
                            {['confirmed', 'shipped', 'in_transit'].includes(enquiry.status) && (
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/buyer/shipping/${enquiry.id}`)}
                                title="Shipping"
                                color="info"
                              >
                                <LocalShipping fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                        
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, enquiry.id)}
                          title="More Actions"
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {filteredEnquiries.length === 0 && enquiries.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              No enquiries match the selected filter.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleAction('chat')}>
          <Chat fontSize="small" sx={{ mr: 1 }} />
          Open Chat
        </MenuItem>
        <MenuItem onClick={() => handleAction('payment')}>
          <Payment fontSize="small" sx={{ mr: 1 }} />
          Make Payment
        </MenuItem>
        <MenuItem onClick={() => handleAction('shipping')}>
          <LocalShipping fontSize="small" sx={{ mr: 1 }} />
          Track Shipping
        </MenuItem>
        <MenuItem onClick={() => handleAction('download')}>
          <Download fontSize="small" sx={{ mr: 1 }} />
          Download Documents
        </MenuItem>
      </Menu>

      {/* Enquiry Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedEnquiry && (
          <>
            <DialogTitle>
              Enquiry Details - #{selectedEnquiry.id}
            </DialogTitle>
            <DialogContent>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold', width: '30%' }}>
                        Enquiry ID
                      </TableCell>
                      <TableCell>#{selectedEnquiry.id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Status
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={selectedEnquiry.status}
                          color={getStatusColor(selectedEnquiry.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Product ID
                      </TableCell>
                      <TableCell>{selectedEnquiry.fruit_id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Quantity
                      </TableCell>
                      <TableCell>{selectedEnquiry.quantity} containers</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        Created Date
                      </TableCell>
                      <TableCell>{formatDate(selectedEnquiry.created_at)}</TableCell>
                    </TableRow>
                    {selectedEnquiry.message && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          Message/Requirements
                        </TableCell>
                        <TableCell>{selectedEnquiry.message}</TableCell>
                      </TableRow>
                    )}
                    {selectedEnquiry.whatsapp_chat_id && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                          WhatsApp Chat ID
                        </TableCell>
                        <TableCell>{selectedEnquiry.whatsapp_chat_id}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setDetailDialogOpen(false);
                  navigate(`/buyer/enquiries/${selectedEnquiry.id}`);
                }}
              >
                View Full Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default BuyerEnquiries;